import { useEffect } from 'react';

interface UseEnterKeyDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  disabled?: boolean;
}

/**
 * Custom hook to handle Enter key press in confirmation dialogs
 * @param isOpen - Whether the dialog is open
 * @param onConfirm - Function to call when Enter is pressed
 * @param disabled - Whether the confirm action is disabled
 */
export const useEnterKeyDialog = ({ isOpen, onConfirm, disabled = false }: UseEnterKeyDialogProps) => {
  useEffect(() => {
    if (!isOpen || disabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle Enter key
      if (event.key !== 'Enter') return;
      
      // Don't trigger if user is typing in an input field or a button is focused
      const activeElement = document.activeElement;
      if (activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.tagName === 'BUTTON' || // Allow focused buttons to be triggered
        (activeElement as HTMLElement).contentEditable === 'true'
      )) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onConfirm();
    };

    document.addEventListener('keydown', handleKeyDown, true); // Use capture phase

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, onConfirm, disabled]);
}; 